import moment from 'moment';
import { create } from 'xmlbuilder2';

const itemMaster = require('./itemMaster.json');

let itemMap = {};

for (let item of itemMaster) {
    itemMap[item.ITEM_NAME] = item.ITEM_CODE;
}

function createXmlFile(payload, patientName, orderId) {
    const xmlContent = payload;
    let filename = `FiliaRX-${patientName}-${orderId}.xml`
    const element = document.createElement("a");
    const file = new Blob([xmlContent], { type: 'text/xml' });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
}

function checkIfMedicineToBeTaken(prescriptionItem, date) {
    const checkDate = moment(date);
    const startDate = moment(prescriptionItem.startDate);
    const endDate = moment(prescriptionItem.endDate);

    if (checkDate.isBefore(startDate) || checkDate.isAfter(endDate)) {
        return {};
    }

    const daysSinceStart = checkDate.diff(startDate, 'days');
    const dayOfWeek = checkDate.format('dddd').toLowerCase();

    switch (prescriptionItem.typeOfAdmin) {
        case 'DATE':
            if (checkDate.isSame(startDate, 'day')) {
                return getAdminTimesAndDoses(prescriptionItem);
            }
            break;
        case 'ALTERNATE':
            if (daysSinceStart % 2 === 0) {
                return getAdminTimesAndDoses(prescriptionItem);
            }
            break;
        case 'DAYS':
            if (prescriptionItem[dayOfWeek]) {
                return getAdminTimesAndDoses(prescriptionItem);
            }
            break;
        default:
            console.warn(`Unknown typeOfAdmin: ${prescriptionItem.typeOfAdmin}`);
    }

    return {};
}

function getAdminTimesAndDoses(prescriptionItem) {
    if (prescriptionItem.time.length === 0) {
        // If no specific times are set, assume it's taken once a day
        return {
            'anytime': {
                dose: prescriptionItem.doseSize,
                adminDescription: 'As prescribed'
            }
        };
    }

    return prescriptionItem.time.reduce((result, time, index) => {
        result[time] = {
            dose: prescriptionItem.doseSize,
            adminDescription: prescriptionItem.meal[index]
        };
        return result;
    }, {});
}

function getTimeCategory(time) {
    const hour = parseInt(time.split(':')[0]);
    if (hour >= 4 && hour < 12) {
        return 'Morning.bmp';
    } else if (hour >= 12 && hour < 17) {
        return 'Afternoon.bmp';
    } else if (hour >= 17 && hour < 20) {
        return 'Evening.bmp';
    } else {
        return 'Night.bmp';
    }
}

export function generateFiliaRxXml(patientName, orderData, createFile = true) {
    const startDate = moment(orderData.startDate);
    const endDate = moment(orderData.endDate);

    const filiaRxObject = {
        filiaServiceRequest: {
            'requestType': '1',
            basic: {
                '@LocationId': 'OP Pharmacy',
                '@machineNumber': '1',
            },
            patient: {
                '@orderType': '0',
                '@startDate': startDate.format('YYYYMMDD'),
                '@orderNumber': orderData.id,
                '@patientID': orderData.mapUserPatient.Patient.patient_id.replaceAll('-', ''),
                '@patientName': patientName,
                pouch: [],
            },
        },
    };

    if (!createFile) {
        delete Object.assign(filiaRxObject.filiaServiceRequest.basic, { '@locationId': filiaRxObject.filiaServiceRequest.basic['@LocationId'] })['@LocationId'];
        delete Object.assign(filiaRxObject.filiaServiceRequest.patient, { '@patientId': filiaRxObject.filiaServiceRequest.patient['@patientID'] })['@patientID'];
    }

    // Iterate over each date in the range [startDate, endDate]
    let currentDate = startDate.clone();
    while (currentDate.isSameOrBefore(endDate)) {
        const pouchesByTime = {};

        // Process each prescription item for the current date
        orderData.prescription.prescriptionItems.forEach((item) => {
            if (item.time.length === 0) {
                return;
            }
            const timesAndDoses = checkIfMedicineToBeTaken(item, currentDate);

            if (Object.keys(timesAndDoses).length > 0) {
                Object.entries(timesAndDoses).forEach(([time, details]) => {
                    if (!pouchesByTime[time]) {
                        pouchesByTime[time] = {
                            '@flag': '1',
                            '@offsetDays': currentDate.diff(startDate, 'days').toString(),
                            '@doseTime': moment(time, 'HH:mm').format('hh:mm A'),
                            '@layout': '1',
                            drug: [],
                            print: [
                                { '@id': '6', '@text': currentDate.format('DD MMMM YYYY') },
                                { '@id': '7', '@text': moment(time, 'HH:mm').format('hh:mm A') },
                                { '@id': '8', '@text': getTimeCategory(time) },
                                { '@id': '3', '@text': currentDate.format('dddd') },
                                { '@id': '2', '@text': orderData.mapUserPatient.Patient.name },
                            ],
                        };
                    }

                    const drugCode = item.item.mnemonic;

                    // Check if the drug already exists in the pouch
                    const existingDrug = pouchesByTime[time].drug.find(d => d['@code'] === drugCode);
                    const existingPrint = pouchesByTime[time].print.find(p => p['@text'].trim() === item.item.name);

                    if (existingDrug) {
                        existingDrug['@val'] = (parseFloat(existingDrug['@val']) + parseFloat(details.dose)).toString().replace('.0', '');
                        if (existingPrint) {
                            let existingDosePrint = pouchesByTime[time].print.find(p => parseInt(p['@id']) === (parseInt(existingPrint['@id']) + 10));
                            existingDosePrint['@text'] = existingDrug['@val'];
                        }
                    } else {

                        // Add the drug with a unique print ID (between 10-17)
                        const drugPrintId = 10 + pouchesByTime[time].drug.length;

                        // Add the drug meal with a unique print ID (between 30-37)
                        const drugMealPrintId = 30 + pouchesByTime[time].drug.length;

                        if (item.meal.length) {
                            pouchesByTime[time].print.push({
                                '@id': drugMealPrintId.toString(),
                                '@text': `${item.meal[0].toLowerCase().includes('after') ? " AFTER" : "BEFORE"}`,
                            });
                        }
                        pouchesByTime[time].print.push({
                            '@id': drugPrintId.toString(),
                            '@text': `${item.item.name}`,
                        });

                        pouchesByTime[time].print.push({
                            '@id': (drugPrintId + 10).toString(),
                            '@text': details.dose.toString(),
                        });

                        pouchesByTime[time].drug.push({
                            '@code': drugCode,
                            '@val': details.dose.toString().replace('.0', ''),
                        });
                    }
                });
            }
        });

        // Sort the pouches by doseTime
        const sortedPouches = Object.entries(pouchesByTime)
            .sort(([timeA], [timeB]) => {
                return timeA.localeCompare(timeB);
            })
            .map(([, pouch]) => pouch);

        sortedPouches.forEach((pouch) => {
            filiaRxObject.filiaServiceRequest.patient.pouch.push(pouch);
        });

        currentDate.add(1, 'day');
    }

    // Generate the XML from the object structure
    const xml = create({ version: '1.0', encoding: 'utf-8' })
        .ele(filiaRxObject)
        .end({ prettyPrint: true });

    createFile && createXmlFile(xml, patientName, orderData.id)

    return xml;
}
